import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
    RefObject,
} from 'react';
import { ReleaseList } from './Display/ReleaseList';
import { usePromise } from '../../../Common/hooks/usePromise';
import { usePrevious } from '../../../Common/hooks/usePrevious';
import { CancelToken } from 'axios';
import { getReleaseList } from '../../api/AGSSApi';
import { ReleaseSearchResult } from '../../models/Release';
import { ErrorPage } from '../../../HubLayout/components/ErrorPage';
import { emitPageLoadMetric } from '../../../Common/util/metricsUtil';
import {
    getEndDateInLocalStorage,
    getMultiStageReleaseSearchValueFromLocalStorage,
    getSelectedDeploymentTypesFromLocalStorage,
    getSelectedEnvironmentNamesFromLocalStorage,
    getSelectedServiceNamesFromLocalStorage,
    getStartDateInLocalStorage,
    setEndDateInLocalStorage,
    setMultiStageReleaseSearchValueInLocalStorage,
    setSelectedDeploymentTypesInLocalStorage,
    setSelectedEnvironmentNamesInLocalStorage,
    setSelectedServiceNamesInLocalStorage,
    setStartDateInLocalStorage,
} from '../../../Common/util/localStorageUtils';

interface IReleaseLatestListPageProps {
    scrollBarRef: RefObject<HTMLDivElement>;
    scrollTop: number;
}
export const ReleaseLatestListPage: React.FC<IReleaseLatestListPageProps> = (
    props: IReleaseLatestListPageProps
) => {
    const [selectedServices, setSelectedServices] = useState<string[]>(
        () => getSelectedServiceNamesFromLocalStorage() || []
    );
    const [selectedEnvironments, setSelectedEnvironments] = useState<string[]>(
        () => getSelectedEnvironmentNamesFromLocalStorage() || []
    );
    const [selectedDeploymentTypes, setSelectedDeploymentTypes] = useState<
        string[]
    >(() => getSelectedDeploymentTypesFromLocalStorage() || []);

    const [searchQuery, setSearchQuery] = useState<string | undefined>(
        () => getMultiStageReleaseSearchValueFromLocalStorage() || ''
    );

    const [selectedProjects, setSelectedProjects] = useState<string[]>([]);

    const [searchValue, setSearchValue] = useState<string | undefined>(
        () => getMultiStageReleaseSearchValueFromLocalStorage() || ''
    );

    const [startDate, setStartDate] = useState<string | undefined>(
        () => getStartDateInLocalStorage() || undefined
    );
    const [endDate, setEndDate] = useState<string | undefined>(
        () => getEndDateInLocalStorage() || undefined
    );

    const [pageNumber, setPageNumber] = useState<number>(1);
    const [hideButton, setHideButton] = useState<boolean>(false);
    const prevPageNumber = usePrevious(pageNumber);
    const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);

    useEffect(() => {
        setMultiStageReleaseSearchValueInLocalStorage(searchValue || '');
        setSelectedEnvironmentNamesInLocalStorage(selectedEnvironments);
        setSelectedServiceNamesInLocalStorage(selectedServices);
        setSelectedDeploymentTypesInLocalStorage(selectedDeploymentTypes);
        setStartDateInLocalStorage(startDate || '');
        setEndDateInLocalStorage(endDate || '');
    }, [
        searchValue,
        selectedEnvironments,
        selectedServices,
        selectedDeploymentTypes,
        startDate,
        endDate,
    ]);

    const getData = useCallback(
        async (cancelToken?: CancelToken) => {
            const releases: ReleaseSearchResult[] = await getReleaseList(
                selectedServices,
                undefined,
                selectedEnvironments,
                selectedDeploymentTypes,
                selectedProjects,
                searchQuery,
                startDate,
                endDate,
                pageNumber,
                cancelToken
            );
            // don't reorder, the server orders depending on params
            return releases;
        },
        // When dependency array changes, usePromise will clear data and restart API.
        [
            selectedServices,
            searchQuery,
            selectedEnvironments,
            selectedDeploymentTypes,
            selectedProjects,
            startDate,
            endDate,
            pageNumber,
        ]
    );
    const [heightDepth, setHeightDepth] = useState<number>(0);
    const [result, error, isLoaded] = usePromise(getData, true);
    // const data = result || [];
    const [data, setData] = useState<ReleaseSearchResult[]>([]);

    useEffect(() => {
        if (pageNumber === 1 && result) {
            if (prevPageNumber !== pageNumber) {
            } else {
                if (result.length !== 0) {
                    setData(result);
                }
                //otherwise return an empty array
                else {
                    result.splice(0, result.length);
                    setData(result);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result]);

    useEffect(() => {
        setHeightDepth(0);
    }, [
        selectedServices,
        selectedEnvironments,
        selectedProjects,
        startDate,
        endDate,
        searchValue,
    ]);

    useEffect(() => {
        if (pageNumber > 1 && result) {
            if (result.length === 0) {
                setHideButton(true);
            } else {
                const newList = data.concat(result);
                setData(newList);
            }
            setHideButton(false);
        }
    }, [pageNumber && result]);

    const scrollMultiplier = 1.1;

    useEffect(() => {
        setTimeout(() => {
            if (props.scrollBarRef.current) {
                props.scrollBarRef.current.scrollTo(
                    0,
                    scrollMultiplier * heightDepth
                );
            }
        }, 50);
    }, [data]);

    useEffect(() => {
        if (props.scrollTop > heightDepth) {
            setHeightDepth(props.scrollTop);
        }
    }, [props.scrollTop]);

    // metric to measure inital page load time
    useEffect(() => {
        var emittedSuccessfully = emitPageLoadMetric(
            'ReleaseLatest',
            process.env.REACT_APP_BUILD_VERSION,
            isFirstLoad
        );

        setIsFirstLoad(emittedSuccessfully);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Silent, in-place refresh used by the auto-refresh timer. Re-fetches every
    // page the user has loaded so far and swaps the results in only on success,
    // so the list never blanks out to a shimmer and transient failures keep the
    // current data visible.
    const silentRefreshInFlight = useRef(false);
    const silentRefreshList = useCallback(async () => {
        if (silentRefreshInFlight.current) {
            return;
        }
        silentRefreshInFlight.current = true;
        try {
            const pages = await Promise.all(
                Array.from({ length: pageNumber }, (_, index) =>
                    getReleaseList(
                        selectedServices,
                        undefined,
                        selectedEnvironments,
                        selectedDeploymentTypes,
                        selectedProjects,
                        searchQuery,
                        startDate,
                        endDate,
                        index + 1
                    )
                )
            );
            setData(([] as ReleaseSearchResult[]).concat(...pages));
        } catch {
            // Keep showing the existing data on a transient refresh failure.
        } finally {
            silentRefreshInFlight.current = false;
        }
    }, [
        selectedServices,
        selectedEnvironments,
        selectedDeploymentTypes,
        selectedProjects,
        searchQuery,
        startDate,
        endDate,
        pageNumber,
    ]);

    return error ? (
        <ErrorPage error={error} />
    ) : (
        <div
            className="ReleaseLatestListPage-root"
            style={{
                padding: '2px 2px 0px 2px',
                border: 'none',
            }}
        >
            <ReleaseList
                list={data}
                isLoaded={isLoaded}
                hideButton={hideButton}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                selectedServices={selectedServices}
                setSelectedServices={setSelectedServices}
                selectedEnvironments={selectedEnvironments}
                setSelectedEnvironments={setSelectedEnvironments}
                selectedDeploymentTypes={selectedDeploymentTypes}
                setSelectedDeploymentTypes={setSelectedDeploymentTypes}
                setSelectedProjects={setSelectedProjects}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                setPageNumber={setPageNumber}
                pageNumber={pageNumber}
                onAutoRefresh={silentRefreshList}
            />
        </div>
    );
};
